import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Material Symbols helper
const Icon = ({ name, className = '', fill = false }) => (
  <span className={`material-symbols-outlined ${fill ? 'fill' : ''} ${className}`}>{name}</span>
);

// ---------- DATA ----------
const PILLARS = [
  { icon: 'psychology', kicker: 'PILLAR 01', title: 'Self-Awareness', analogy: 'HEALTH', desc: 'Recognize your own emotions. Understand triggers. See how they shape decisions.' },
  { icon: 'balance', kicker: 'PILLAR 02', title: 'Self-Regulation', analogy: 'MONEY', desc: 'Manage disruptive emotions. Respond, do not react. Pause, process, choose.' },
  { icon: 'groups', kicker: 'PILLAR 03', title: 'Social Awareness', analogy: 'FAMILY', desc: 'Sense, understand, and react to others. Read the room. Feel what others feel.' },
  { icon: 'diversity_3', kicker: 'PILLAR 04', title: 'Social Skills', analogy: 'FRIENDS', desc: 'Build relationships. Inspire and influence. Turn emotional awareness into impact.' },
];

const EMOTIONS = ['Anger', 'Anxiety', 'Depression', 'Love', 'Fear', 'Joy'];


const ORG_FEATURES = [
  { icon: 'analytics', text: 'Team Analytics & Reporting' },
  { icon: 'clinical_notes', text: 'Industry-specific assessments' },
  { icon: 'verified_user', text: 'CTO / CHRO data export' },
  { icon: 'monitoring', text: 'Real-time culture monitoring' },
];

// ---------- COMPONENT ----------
const Landing = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Scroll-reveal
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('in-view'); io.unobserve(e.target); }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-abyssal-teal text-[#1d1d1f] font-sans antialiased" data-testid="landing-page">
      {/* ============ NAV ============ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                <circle cx="9" cy="12" r="3.5" />
                <path d="M15 8 Q 19 6, 21 4" />
                <path d="M14.5 12 L 22 12" />
                <path d="M15 16 Q 19 18, 21 20" />
              </svg>
            </div>
            <span className="text-[17px] font-bold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/emotions" className="text-[12px] font-normal text-[#424245] hover:text-[#1d1d1f] transition-colors" data-testid="nav-library">Library</Link>
            <a href="#ei-for-women" className="text-[13px] font-semibold text-[#1d1d1f] hover:text-current-teal transition-colors" data-testid="nav-women">Women</a>
            <a href="#ei-for-men" className="text-[13px] font-semibold text-[#1d1d1f] hover:text-current-teal transition-colors" data-testid="nav-men">Men</a>
            <a href="#ei-for-seniors" className="text-[13px] font-semibold text-[#1d1d1f] hover:text-current-teal transition-colors" data-testid="nav-seniors">Seniors</a>
            <a href="#ei-for-youth" className="text-[13px] font-semibold text-[#1d1d1f] hover:text-current-teal transition-colors" data-testid="nav-youth">Youth</a>
            <a href="#organizations" className="text-[12px] font-normal text-[#424245] hover:text-[#1d1d1f] transition-colors" data-testid="nav-orgs">Organizations</a>
            <a href="#method" className="text-[13px] font-semibold text-[#1d1d1f] hover:text-current-teal transition-colors" data-testid="nav-method">Method</a>
          </div>

          <div className="flex items-center gap-4">
            <button className="md:hidden p-1 text-[#1d1d1f]" onClick={() => setMobileNavOpen(v => !v)} aria-label="Menu" data-testid="nav-mobile-toggle">
              <Icon name={mobileNavOpen ? 'close' : 'menu'} className="!text-[20px]" />
            </button>
          </div>
        </div>
      </nav>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 top-14 z-40 bg-white overflow-y-auto" data-testid="mobile-nav-panel">
          <div className="flex flex-col px-6 pt-8 pb-10 gap-1">
            <Link to="/emotions" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-medium text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-library">Library</Link>
            <a href="#ei-for-women" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-semibold text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-women">Women</a>
            <a href="#ei-for-men" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-semibold text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-men">Men</a>
            <a href="#ei-for-seniors" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-semibold text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-seniors">Seniors</a>
            <a href="#ei-for-youth" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-semibold text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-youth">Youth</a>
            <a href="#organizations" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-medium text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-orgs">Organizations</a>
            <a href="#method" onClick={() => setMobileNavOpen(false)} className="text-[22px] font-semibold text-[#1d1d1f] py-3.5 border-b border-black/5" data-testid="mobile-nav-method">Method</a>
          </div>
        </div>
      )}

      {/* ============ THE MINDPULSEI METHOD ============ */}
      <section id="method" className="relative pt-32 md:pt-44 pb-24 md:pb-36" style={{ background: 'linear-gradient(180deg, #dbe6f7 0%, #f5f5f7 22%)' }} data-testid="method-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div className="relative text-center max-w-[820px] mx-auto mb-16 space-y-6" data-reveal>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/33c4e8de-ac5b-4780-ba59-0e4c618fd325/images/d189127a1abdb3c007078a585a9a045ceb3bb318f57d371ce74f3ff8eb4b45ab.jpeg"
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[420px] mix-blend-multiply opacity-80"
              style={{
                maskImage: 'radial-gradient(circle, black 42%, transparent 68%)',
                WebkitMaskImage: 'radial-gradient(circle, black 42%, transparent 68%)',
                animation: 'brainFloat 7s ease-in-out infinite'
              }}
              data-testid="method-brain-image"
            />
            <p className="relative text-current-teal text-[15px] font-medium tracking-tight">What we do</p>
            <h2 className="relative text-[#1d1d1f]" style={{ fontSize: 'clamp(38px, 5.8vw, 72px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600, textShadow: '0 0 24px rgba(245,245,247,0.9), 0 0 48px rgba(245,245,247,0.7)' }}>
              The mindpulsei method.
            </h2>
            <p className="relative text-[#515154] mx-auto" style={{ fontSize: 'clamp(16px, 1.3vw, 20px)', lineHeight: 1.45, letterSpacing: '-0.005em', maxWidth: '640px', textShadow: '0 0 18px rgba(245,245,247,0.9)' }}>
              Four interconnected steps. One wholesome, balanced approach to emotional wellbeing.
            </p>
          </div>

          {/* Four steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {/* Step 1 — Body */}
            <div
              data-reveal
              className="relative overflow-hidden rounded-[28px] p-6 md:p-10 bg-white border border-black/5 flex flex-col justify-between min-h-[280px] transition-all hover:border-black/10 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              data-testid="method-tile-body"
            >
              <img
                src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=700&q=60"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 w-[280px] h-[280px] object-cover opacity-[0.12] blur-[1px]"
                style={{ maskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)' }}
              />
              <div className="pointer-events-none absolute -right-24 -bottom-24 w-[280px] h-[280px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,168,120,0.28) 0%, rgba(255,168,120,0) 70%)' }} />
              <div className="relative flex items-start justify-between">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
                  <Icon name="self_improvement" className="text-current-teal !text-[22px]" />
                </div>
                <span className="text-[#86868b] text-[13px] font-semibold tracking-widest">01</span>
              </div>
              <div className="relative">
                <h3 className="text-[#1d1d1f] mt-8" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  Body.
                </h3>
                <p className="text-[#515154] text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  We begin with the whole person &mdash; mind included. First, we take the time to understand how your mind works, then shape every course and practice around you and what you truly need.
                </p>
              </div>
            </div>

            {/* Step 2 — Truth */}
            <div
              data-reveal
              style={{ transitionDelay: '60ms' }}
              className="relative overflow-hidden rounded-[28px] p-6 md:p-10 bg-white border border-black/5 flex flex-col justify-between min-h-[280px] transition-all hover:border-black/10 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              data-testid="method-tile-truth"
            >
              <img
                src="https://images.unsplash.com/photo-1476611317561-60117649dd94?auto=format&fit=crop&w=700&q=60"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 w-[280px] h-[280px] object-cover opacity-[0.14] blur-[1px]"
                style={{ maskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)' }}
              />
              <div className="pointer-events-none absolute -right-24 -bottom-24 w-[280px] h-[280px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(120,168,255,0.28) 0%, rgba(120,168,255,0) 70%)' }} />
              <div className="relative flex items-start justify-between">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
                  <Icon name="visibility" className="text-current-teal !text-[22px]" />
                </div>
                <span className="text-[#86868b] text-[13px] font-semibold tracking-widest">02</span>
              </div>
              <div className="relative">
                <h3 className="text-[#1d1d1f] mt-8" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  Truth.
                </h3>
                <p className="text-[#515154] text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Most of us live inside our own version of the truth. In this step, you learn to see your emotions honestly &mdash; to name them, face them, and understand them as they truly are.
                </p>
              </div>
            </div>

            {/* Step 3 — Soul */}
            <div
              data-reveal
              style={{ transitionDelay: '120ms' }}
              className="relative overflow-hidden rounded-[28px] p-6 md:p-10 bg-white border border-black/5 flex flex-col justify-between min-h-[280px] transition-all hover:border-black/10 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              data-testid="method-tile-soul"
            >
              <img
                src="https://images.unsplash.com/photo-1518281420975-50db6e5d0a97?auto=format&fit=crop&w=700&q=60"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 w-[280px] h-[280px] object-cover opacity-[0.14] blur-[1px]"
                style={{ maskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)' }}
              />
              <div className="pointer-events-none absolute -right-24 -bottom-24 w-[280px] h-[280px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(190,150,255,0.28) 0%, rgba(190,150,255,0) 70%)' }} />
              <div className="relative flex items-start justify-between">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
                  <Icon name="spa" className="text-current-teal !text-[22px]" />
                </div>
                <span className="text-[#86868b] text-[13px] font-semibold tracking-widest">03</span>
              </div>
              <div className="relative">
                <h3 className="text-[#1d1d1f] mt-8" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  Soul.
                </h3>
                <p className="text-[#515154] text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  A dedicated chapter on spirituality. Through simple mind practices, you reconnect with positive energy and steadier, more uplifting vibrations.
                </p>
              </div>
            </div>

            {/* Step 4 — Senses */}
            <div
              data-reveal
              style={{ transitionDelay: '180ms' }}
              className="relative overflow-hidden rounded-[28px] p-6 md:p-10 bg-white border border-black/5 flex flex-col justify-between min-h-[280px] transition-all hover:border-black/10 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              data-testid="method-tile-senses"
            >
              <img
                src="https://images.unsplash.com/photo-1518622358385-8ea7d0794bf6?auto=format&fit=crop&w=700&q=60"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 w-[280px] h-[280px] object-cover opacity-[0.14] blur-[1px]"
                style={{ maskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at top right, black 30%, transparent 75%)' }}
              />
              <div className="pointer-events-none absolute -right-24 -bottom-24 w-[280px] h-[280px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(120,220,200,0.30) 0%, rgba(120,220,200,0) 70%)' }} />
              <div className="relative flex items-start justify-between">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
                  <Icon name="hearing" className="text-current-teal !text-[22px]" />
                </div>
                <span className="text-[#86868b] text-[13px] font-semibold tracking-widest">04</span>
              </div>
              <div className="relative">
                <h3 className="text-[#1d1d1f] mt-8" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  Senses.
                </h3>
                <p className="text-[#515154] text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  No two people perceive the world the same way. This step explores how you look at people, situations, and yourself &mdash; and helps you understand why.
                </p>
              </div>
            </div>
          </div>

          {/* Closing statement */}
          <div
            data-reveal
            className="mt-4 md:mt-5 rounded-[28px] p-6 md:p-12 text-center text-white"
            style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}
            data-testid="method-closing"
          >
            <p className="text-white/80 text-[13px] font-medium tracking-tight mb-3">One connected whole</p>
            <p className="mx-auto max-w-[760px]" style={{ fontSize: 'clamp(20px, 2.2vw, 30px)', lineHeight: 1.25, letterSpacing: '-0.015em', fontWeight: 600 }}>
              A first-of-its-kind wholesome approach, designed for real results.
            </p>
            <p className="text-white/85 mx-auto max-w-[640px] mt-4" style={{ fontSize: 'clamp(15px, 1.2vw, 18px)', lineHeight: 1.5, letterSpacing: '-0.005em' }}>
              Body, truth, soul, and senses are deeply interconnected. Together, they bring your emotions into lasting balance.
            </p>
          </div>
        </div>
      </section>

      {/* ============ EI FOR WOMEN ============ */}
      <section id="ei-for-women" className="relative py-24 md:py-36" style={{ background: '#f5f5f7' }} data-testid="ei-for-women-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          {/* Split hero: image left, copy right */}
          <div className="rounded-[32px] overflow-hidden bg-white border border-black/5 mb-4 md:mb-5" data-testid="ei-women-hero">
            <div className="grid md:grid-cols-2 items-stretch">
              <div className="aspect-[4/5] md:aspect-auto overflow-hidden" style={{ background: '#eaeaef' }}>
                <img
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80&auto=format&fit=crop"
                  alt="Confident woman leader"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:p-14 flex flex-col justify-center">
                <p className="text-current-teal text-[15px] font-medium tracking-tight mb-4">For Women</p>
                <h2 className="text-[#1d1d1f] mb-5" style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
                  Emotional intelligence,<br />made for her.
                </h2>
                <p className="text-[#515154] text-[15px] md:text-[17px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Women lead, mother, mentor, and manage &mdash; often all in the same day. mindpulsei is a growth practice designed for that reality: assertive without aggressive, present without depleted, ambitious without apologetic.
                </p>
                <p className="text-[#6e6e73] text-[13px] mt-4 tracking-tight">Bar-On EQ-i · McKinsey Women in the Workplace 2023</p>
                <div className="mt-8">
                  <Link to="/for-women">
                    <button className="px-6 py-3 rounded-full text-[15px] font-medium text-white transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }} data-testid="women-explore-btn">
                      Explore more
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============ EI FOR MEN ============ */}
      <section id="ei-for-men" className="relative py-24 md:py-36" data-testid="ei-for-men-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          {/* Split hero: copy left, image right (mirrors seniors for rhythm) */}
          <div className="rounded-[32px] overflow-hidden bg-white border border-black/5" data-testid="ei-men-hero">
            <div className="grid md:grid-cols-2 items-stretch">
              <div className="p-8 md:p-14 flex flex-col justify-center order-2 md:order-1">
                <p className="text-current-teal text-[15px] font-medium tracking-tight mb-4">For Men</p>
                <h2 className="text-[#1d1d1f] mb-5" style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
                  Strength that knows<br />what it feels.
                </h2>
                <p className="text-[#515154] text-[15px] md:text-[17px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Men are taught to provide, protect, and endure &mdash; rarely to name what they feel. The cost shows up quietly: isolation, anger, silent stress. mindpulsei is a practical, no-nonsense practice that turns emotional awareness into real strength.
                </p>
                <p className="text-[#6e6e73] text-[13px] mt-4 tracking-tight">APA Men &amp; Masculinity &middot; Movember Men&rsquo;s Health</p>
                <div className="mt-8">
                  <Link to="/for-men">
                    <button className="px-6 py-3 rounded-full text-[15px] font-medium text-white transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }} data-testid="men-explore-btn">
                      Explore more
                    </button>
                  </Link>
                </div>
              </div>
              <div className="aspect-[4/5] md:aspect-auto overflow-hidden order-1 md:order-2" style={{ background: '#eaeaef' }}>
                <img
                  src="https://images.unsplash.com/photo-1763293203875-4883b6716a8c?w=1200&q=80&auto=format&fit=crop"
                  alt="Man in calm reflection by the sea at sunset"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============ EI FOR SENIOR CITIZENS ============ */}
      <section id="ei-for-seniors" className="relative py-24 md:py-36" data-testid="ei-for-seniors-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          {/* Split hero: copy left, image right (mirrors women section for rhythm) */}
          <div className="rounded-[32px] overflow-hidden bg-white border border-black/5 mb-4 md:mb-5" data-testid="ei-seniors-hero">
            <div className="grid md:grid-cols-2 items-stretch">
              <div className="p-8 md:p-14 flex flex-col justify-center order-2 md:order-1">
                <p className="text-current-teal text-[15px] font-medium tracking-tight mb-4">For Senior Citizens</p>
                <h2 className="text-[#1d1d1f] mb-5" style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
                  The chapter your life<br />was writing toward.
                </h2>
                <p className="text-[#515154] text-[15px] md:text-[17px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Retirement isn&rsquo;t the finish line. It&rsquo;s the season with the most emotional wisdom &mdash; and the most to give. mindpulsei helps you rebuild purpose, deepen the bonds that matter, and turn a lifetime of experience into legacy.
                </p>
                <p className="text-[#6e6e73] text-[13px] mt-4 tracking-tight">Harvard Study of Adult Development &middot; Carstensen, Stanford Longevity</p>
                <div className="mt-8">
                  <Link to="/for-seniors">
                    <button className="px-6 py-3 rounded-full text-[15px] font-medium text-white transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }} data-testid="seniors-explore-btn">
                      Explore more
                    </button>
                  </Link>
                </div>
              </div>
              <div className="aspect-[4/5] md:aspect-auto overflow-hidden order-1 md:order-2" style={{ background: '#eaeaef' }}>
                <img
                  src="https://images.unsplash.com/photo-1447452001602-7090c7ab2db3?w=1200&q=80&auto=format&fit=crop"
                  alt="Warm senior couple sharing a moment"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* ============ EI FOR YOUTH (15-30) ============ */}
      <section id="ei-for-youth" className="relative py-24 md:py-36" data-testid="ei-for-youth-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          {/* Split hero: image left, copy right */}
          <div className="rounded-[32px] overflow-hidden bg-white border border-black/5 mb-4 md:mb-5" data-testid="ei-youth-hero">
            <div className="grid md:grid-cols-2 items-stretch">
              <div className="aspect-[4/5] md:aspect-auto overflow-hidden" style={{ background: '#eaeaef' }}>
                <img
                  src="https://images.unsplash.com/photo-1517841905240-472988babdf9?w=1200&q=80&auto=format&fit=crop"
                  alt="Young person in a moment of reflection"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:p-14 flex flex-col justify-center">
                <p className="text-current-teal text-[15px] font-medium tracking-tight mb-4">For Youth &middot; Ages 15&ndash;30</p>
                <h2 className="text-[#1d1d1f] mb-5" style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
                  The decade that<br />writes the next fifty.
                </h2>
                <p className="text-[#515154] text-[15px] md:text-[17px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  The brain is still forming until 25. Identity, career, relationships, and mental health are all set here &mdash; often at speeds no one taught you to slow down. Emotional intelligence is the one skill that compounds across all four.
                </p>
                <p className="text-[#6e6e73] text-[13px] mt-4 tracking-tight">WHO Mental Health Report &middot; Kessler et al. &middot; Petrides &amp; Furnham</p>
                <div className="mt-8">
                  <Link to="/for-youth">
                    <button className="px-6 py-3 rounded-full text-[15px] font-medium text-white transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }} data-testid="youth-explore-btn">
                      Explore more
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============ FOR ORGANIZATIONS ============ */}
      <section id="organizations" className="relative py-24 md:py-36" data-testid="organizations-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div className="relative rounded-[32px] overflow-hidden" style={{ background: '#fbfbfd' }}>
            <div className="relative z-10 px-6 md:px-16 py-20 md:py-24 grid lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-3 space-y-6" data-reveal>
                <p className="text-current-teal text-[15px] font-medium tracking-tight">For Organizations</p>
                <h2 className="text-[#1d1d1f]" style={{ fontSize: 'clamp(38px, 5.4vw, 68px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
                  Empower your team<br />with EI.
                </h2>
                <p className="text-[#515154]" style={{ fontSize: 'clamp(16px, 1.3vw, 19px)', lineHeight: 1.45, letterSpacing: '-0.005em', maxWidth: '560px' }}>
                  Industry-specific leadership assessments, team analytics, and data export — built for Healthcare, IT, Finance, Education, Retail, and HR.
                </p>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2">
                  <Link to="/corporate/register" data-testid="org-register-btn">
                    <button className="px-6 py-3 rounded-full text-[15px] font-medium bg-current-teal hover:bg-ice-mist text-white transition-colors">
                      Register organization
                    </button>
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-reveal style={{ transitionDelay: '120ms' }}>
                {ORG_FEATURES.map(f => (
                  <div key={f.text} className="rounded-2xl p-4 flex items-start gap-3 bg-black/[0.04] border border-black/5">
                    <Icon name={f.icon} className="text-current-teal !text-[20px] flex-shrink-0 mt-0.5" />
                    <p className="text-[#1d1d1f] text-[13px]" style={{ lineHeight: 1.4, letterSpacing: '-0.005em' }}>{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Why invest in EI — 4 benefits */}
          <div className="mt-4 md:mt-5">
            <p className="text-center text-[#6e6e73] text-[13px] font-medium tracking-tight mb-6" data-reveal>
              Why forward-thinking organizations invest in emotional intelligence
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              <div data-reveal className="rounded-[24px] p-7 bg-white border border-black/5 transition-colors hover:border-black/10" data-testid="org-benefit-1">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-6 bg-current-teal/10">
                  <Icon name="trending_up" className="text-current-teal !text-[22px]" />
                </div>
                <h3 className="text-[#1d1d1f] text-[18px] font-semibold mb-2" style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Higher performance.
                </h3>
                <p className="text-[#515154] text-[13px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Teams with high EI communicate clearly, decide faster, and sustain focus under pressure &mdash; turning emotional skill into measurable output.
                </p>
              </div>

              <div data-reveal style={{ transitionDelay: '60ms' }} className="rounded-[24px] p-7 bg-white border border-black/5 transition-colors hover:border-black/10" data-testid="org-benefit-2">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-6 bg-current-teal/10">
                  <Icon name="favorite" className="text-current-teal !text-[22px]" />
                </div>
                <h3 className="text-[#1d1d1f] text-[18px] font-semibold mb-2" style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Stronger retention.
                </h3>
                <p className="text-[#515154] text-[13px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  People stay where they feel understood. EI-led cultures see less burnout and lower regretted attrition &mdash; your best people keep choosing you.
                </p>
              </div>

              <div data-reveal style={{ transitionDelay: '120ms' }} className="rounded-[24px] p-7 bg-white border border-black/5 transition-colors hover:border-black/10" data-testid="org-benefit-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-6 bg-current-teal/10">
                  <Icon name="workspace_premium" className="text-current-teal !text-[22px]" />
                </div>
                <h3 className="text-[#1d1d1f] text-[18px] font-semibold mb-2" style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Better leaders.
                </h3>
                <p className="text-[#515154] text-[13px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Emotional intelligence is the strongest predictor of leadership success. Investing in it grows managers into leaders people want to follow.
                </p>
              </div>

              <div data-reveal style={{ transitionDelay: '180ms' }} className="rounded-[24px] p-7 bg-white border border-black/5 transition-colors hover:border-black/10" data-testid="org-benefit-4">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-6 bg-current-teal/10">
                  <Icon name="diversity_3" className="text-current-teal !text-[22px]" />
                </div>
                <h3 className="text-[#1d1d1f] text-[18px] font-semibold mb-2" style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Healthier culture.
                </h3>
                <p className="text-[#515154] text-[13px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Fewer conflicts, faster recovery from setbacks, and real psychological safety &mdash; the foundation every high-trust organization is built on.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY MINDPULSE EI ============ */}
      <section id="why-mindpulsei" className="relative py-24 md:py-36" data-testid="why-mindpulsei-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div className="text-center max-w-[820px] mx-auto mb-16 space-y-6" data-reveal>
            <p className="text-current-teal text-[15px] font-medium tracking-tight">Why mindpulsei</p>
            <h2 className="text-[#1d1d1f]" style={{ fontSize: 'clamp(38px, 5.8vw, 72px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }}>
              Not another wellness app.<br />A results contract.
            </h2>
            <p className="text-[#515154] mx-auto" style={{ fontSize: 'clamp(16px, 1.3vw, 20px)', lineHeight: 1.45, letterSpacing: '-0.005em', maxWidth: '640px' }}>
              We measure what matters, guarantee the outcome, and treat your growth as a wholesome discipline &mdash; mind, body, relationships, and finance.
            </p>
          </div>

          {/* Bento grid: row 1 = 2 large tiles, row 2 = 3 smaller tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
            {/* Tile 1 — Result-oriented plans */}
            <div
              data-reveal
              className="rounded-[28px] p-6 md:p-10 border border-black/5 flex flex-col justify-between min-h-[280px] transition-colors hover:border-black/10"
              style={{ background: '#ffffff' }}
              data-testid="why-tile-1"
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
                <Icon name="target" className="text-current-teal !text-[22px]" />
              </div>
              <div>
                <h3 className="text-[#1d1d1f] mt-8" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  Result-oriented plans.
                </h3>
                <p className="text-[#515154] text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  Every engagement starts with a measurable baseline and a milestone plan. You know what you&rsquo;re paying for, what you&rsquo;ll get, and by when.
                </p>
              </div>
            </div>

            {/* Tile 2 — Money-back guarantee (highlighted) */}
            <div
              data-reveal
              style={{ transitionDelay: '80ms', background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}
              className="rounded-[28px] p-6 md:p-10 flex flex-col justify-between min-h-[280px] text-white"
              data-testid="why-tile-2"
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur">
                <Icon name="verified" className="text-white !text-[22px]" />
              </div>
              <div>
                <p className="text-white/80 text-[12px] font-medium tracking-tight mb-2">Our commitment</p>
                <h3 className="text-white" style={{ fontSize: 'clamp(24px, 2.4vw, 32px)', lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 600 }}>
                  No result. Full refund.
                </h3>
                <p className="text-white/90 text-[15px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                  If you don&rsquo;t see measurable movement on the metrics we agreed to, you get 100% of your money back. Zero risk, zero fine print.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {/* Tile 3 — Wholesome approach */}
            <div
              data-reveal
              className="rounded-[28px] p-8 border border-black/5 min-h-[260px] flex flex-col transition-colors hover:border-black/10"
              style={{ background: '#ffffff' }}
              data-testid="why-tile-3"
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-8 bg-current-teal/10">
                <Icon name="spa" className="text-current-teal !text-[22px]" />
              </div>
              <h3 className="text-[#1d1d1f]" style={{ fontSize: '22px', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 600 }}>
                A wholesome approach.
              </h3>
              <p className="text-[#515154] text-[14px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                Health, self-peace, balance, and finance &mdash; treated as one system. Real change happens when all four move together.
              </p>
            </div>

            {/* Tile 4 — Better mind better life */}
            <div
              data-reveal
              style={{ transitionDelay: '80ms', background: '#ffffff' }}
              className="rounded-[28px] p-8 border border-black/5 min-h-[260px] flex flex-col transition-colors hover:border-black/10"
              data-testid="why-tile-4"
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-8 bg-current-teal/10">
                <Icon name="psychology_alt" className="text-current-teal !text-[22px]" />
              </div>
              <h3 className="text-[#1d1d1f]" style={{ fontSize: '22px', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 600 }}>
                Better mind. Better life.
              </h3>
              <p className="text-[#515154] text-[14px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                Upgrade the operating system, not the wallpaper. Clearer thinking, calmer reactions, and decisions you&rsquo;re proud of.
              </p>
            </div>

            {/* Tile 5 — Alive every moment */}
            <div
              data-reveal
              style={{ transitionDelay: '160ms', background: '#ffffff' }}
              className="rounded-[28px] p-8 border border-black/5 min-h-[260px] flex flex-col transition-colors hover:border-black/10"
              data-testid="why-tile-5"
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-8 bg-current-teal/10">
                <Icon name="favorite" className="text-current-teal !text-[22px]" />
              </div>
              <h3 className="text-[#1d1d1f]" style={{ fontSize: '22px', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 600 }}>
                Not just to live. To be alive.
              </h3>
              <p className="text-[#515154] text-[14px] mt-3" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                We believe in showing up fully &mdash; in every meeting, every meal, every moment. Presence is the practice we&rsquo;re here to teach.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative pt-16 pb-10 border-t border-black/10" style={{ background: '#f5f5f7' }} data-testid="footer">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div className="flex flex-col md:flex-row gap-10 md:items-start md:justify-between">
            <div className="space-y-4 max-w-md">
              <Link to="/" className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-md flex items-center justify-center bg-gradient-to-br from-current-teal to-ice-mist">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
                    <circle cx="9" cy="12" r="3.5" />
                    <path d="M15 8 Q 19 6, 21 4" />
                    <path d="M14.5 12 L 22 12" />
                    <path d="M15 16 Q 19 18, 21 20" />
                  </svg>
                </div>
                <span className="text-[14px] font-semibold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
              </Link>
              <p className="text-[#86868b] text-[13px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                Advancing human potential through precision emotional intelligence.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-[#a1a1a6] text-[11px] font-medium tracking-tight mb-1">Get in touch</p>
              <a href="tel:+919717178733" className="text-[#1d1d1f] text-[13px] hover:text-current-teal transition-colors flex items-center gap-2" data-testid="footer-call">
                <Icon name="call" className="!text-[14px]" /> +91 97171 78733
              </a>
              <a href="mailto:hello@mindpulsei.com" className="text-[#1d1d1f] text-[13px] hover:text-current-teal transition-colors flex items-center gap-2" data-testid="footer-email">
                <Icon name="mail" className="!text-[14px]" /> hello@mindpulsei.com
              </a>
              <a href="https://wa.me/919717178733" target="_blank" rel="noopener noreferrer" className="text-[#1d1d1f] text-[13px] hover:text-current-teal transition-colors flex items-center gap-2" data-testid="footer-whatsapp">
                <Icon name="forum" className="!text-[14px]" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-black/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-[#a1a1a6] text-[12px] tracking-tight">© {new Date().getFullYear()} mindpulsei. All rights reserved.</p>
            <div className="flex items-center gap-2 text-[#a1a1a6] text-[11px] tracking-tight">
              <Icon name="verified" className="!text-[13px] text-current-teal" />
              Privacy-first
            </div>
          </div>
        </div>
      </footer>

      {/* ============ REVIEW MODAL removed with testimonials section ============ */}
    </div>
  );
};

export default Landing;
